import { plainToClass } from "class-transformer";
import isEmpty from "lodash/isEmpty";

import * as classValidator from "class-validator";

import type { ClassConstructor, ClassTransformOptions } from "class-transformer";
import type { ValidationOptions as ClassValidatorOptions, ValidationError } from "class-validator";
import type { FormikHelpers, FormikProps } from "formik";

export * from "class-validator";

export interface FormikType<T extends Record<string, string>> extends FormikProps<T> {
  setFieldTouched: (field: keyof T, isTouched?: boolean, shouldValidate?: boolean) => Promise<void>;
  setFieldValue: (field: keyof T, value: any, shouldValidate?: boolean) => Promise<void>;
}

export type FormikErrors = Record<string, string | Record<string, string>>;

function mapErrorsToFormikFormat(errors: ValidationError[]) {
  const result: any = {};

  for (const error of errors) {
    let validationMessage = "";
    let highestPriority = 0;

    for (const constrain in error.constraints) {
      if (error.contexts && error.contexts[constrain]) {
        if (!highestPriority || error.contexts[constrain].errorDisplayPriority > highestPriority) {
          highestPriority = error.contexts[constrain].errorDisplayPriority;
          validationMessage = error.constraints[constrain];
        }
      } else {
        validationMessage = error.constraints[constrain];
      }
    }
    // TODO Figure out how to translate these
    if (validationMessage) {
      if (
        validationMessage.indexOf("must be one of the following values") > -1 ||
        validationMessage.indexOf("must contain at least") > -1 ||
        validationMessage.indexOf("mongodb id") > -1 ||
        validationMessage.indexOf("should not be empty") > -1 ||
        validationMessage.indexOf("valid enum value") > -1 ||
        validationMessage.indexOf("must be a string") > -1 ||
        validationMessage.indexOf("should not be null") > -1
      ) {
        validationMessage = "This field is required";
      } else if (
        validationMessage.indexOf("number conforming to the specified") > -1 ||
        validationMessage.indexOf("must be longer than") > -1 ||
        validationMessage.indexOf("must be shorter than") > -1 ||
        validationMessage.indexOf("must not be less than") > -1 ||
        validationMessage.indexOf("must match") > -1
      ) {
        validationMessage = "This value is invalid";
      } else if (validationMessage.indexOf("must be a valid phone number") > -1) {
        validationMessage = "Please enter a valid phone number";
      } else if (validationMessage.indexOf("must be an email") > -1) {
        validationMessage = "Invalid email address";
      }
    }

    result[error.property] = validationMessage;

    if (error.children?.length) {
      const nestedErrors = mapErrorsToFormikFormat(error.children);
      result[error.property] = nestedErrors;
    }
  }

  return result;
}

export interface ValidationOptions {
  validator?: ClassValidatorOptions & { forbidUnknownValues?: boolean; skipMissingProperties?: boolean };
  transformer?: ClassTransformOptions & { enableImplicitConversion?: boolean };
}

function validateSync<T extends object>(
  classType: ClassConstructor<T>,
  data: string | object | object[],
  options?: ValidationOptions
): T {
  let object: object;

  if (typeof data === "string") {
    object = JSON.parse(data);
  } else if (typeof data === "object" && data !== null) {
    object = data;
  } else {
    throw new Error(
      `Invalid \`data\` parameter of type "${typeof data}".` +
        "Only string, plain object and array of plain objects are valid."
    );
  }

  const classObject = plainToClass(classType, object, options?.transformer);

  if (Array.isArray(classObject)) {
    const errors = classObject.map((objectElement) => classValidator.validateSync(objectElement, options?.validator));

    if (errors.some((errors) => errors.length)) {
      throw errors;
    }

    return classObject;
  }

  const errors = classValidator.validateSync(classObject, options?.validator);

  if (errors.length) {
    throw errors;
  }

  return classObject;
}

export type ValidatorOptions = ValidationOptions & { debug?: boolean };

export function formikValidate<T extends object>(classType: ClassConstructor<T>, data: T, options?: ValidatorOptions) {
  try {
    validateSync(classType, data, options);
    return {};
  } catch (error) {
    if (options?.debug) {
      console.error(`[formikValidate]: Validation failed with the following error(s): ${error}`);
    }

    if (error instanceof Error) {
      throw error;
    }

    return mapErrorsToFormikFormat(error as ValidationError[]);
  }
}

export function createValidator<T extends object>(
  classConstructor: ClassConstructor<T>,
  initialOptions?: ValidatorOptions
) {
  return (data: T, options?: ValidationOptions) => {
    const isDebug = initialOptions?.debug === undefined ? process.env.NODE_ENV === "development" : initialOptions.debug;

    return formikValidate<T>(classConstructor, data, {
      ...options,
      debug: isDebug,
      validator: {
        forbidUnknownValues: true,
        ...initialOptions?.validator,
        ...options?.validator
      },
      transformer: {
        enableImplicitConversion: true,
        ...initialOptions?.transformer,
        ...options?.transformer
      }
    });
  };
}

export function validateForm<T extends object>(values: T, formikHelpers: FormikHelpers<T>) {
  const errors = formikHelpers.validateForm(values);
  return { errors, isValid: isEmpty(errors) };
}
